import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import DataTable from "../../../components/Content/DataTable";
// Date & Time
import Datetime from "react-datetime";
import moment from "moment";

import { getAlert, getData, getDataWithComp } from "../../../../utils/SamuiUtils";
import {
    formatThaiDateUi,
    formatStringDateToDate,
} from "../../../../utils/DateUtils";
import DateFilterTable from "../../../components/Content/DateFilterTable";

function Main({
    masterList,
    detailList,
    statusColours,
    name,
    onPageInsert,
    onRowSelected,
    getReport,
}) {
    const [dataMasterList, setDataMasterList] = useState([]);
    const [dataDetailList, setDataDetailList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(16);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredItems, setFilteredItems] = useState([]);

    // Filter by date & Sort by field
    const [startDate, setStartDate] = useState(
        moment().startOf("month").add(543, 'year').format('YYYY-MM-DD')
    );
    const [endDate, setEndDate] = useState(moment().add(543, 'year').format('YYYY-MM-DD'));
    const [sortOrder, setSortOrder] = useState("");
    const [sortField, setSortField] = useState("");
    const [selectedDateType, setSelectedDateType] = useState("1");
    const [allDates, setAllDates] = useState(false);

    useEffect(() => {
        initialize();
    }, [masterList, detailList]);

    useEffect(() => {
        setCurrentPage(1);
        filterItems();
    }, [searchTerm, dataMasterList]);

    // Remove reset of sortField on date change to persist user sorting preference
    // useEffect(() => {
    //     setSortField("");
    // }, [startDate, endDate]);

    const initialize = async () => {
        try {
            if (masterList && masterList.length > 0) {
                // Fetch AR List for Name mapping
                const arList = await getDataWithComp("Tb_Set_AR", "");
                const arMap = {};
                if (arList && Array.isArray(arList)) {
                    arList.forEach(ar => {
                        arMap[ar.AR_Code] = ar.AR_Name;
                    });
                }

                const processedList = masterList.map((item) => {
                    // Fix Date: If year > 2400 (Thai year), subtract 543 to make it Gregorian for DataTable
                    let fixedDate = item.Rcv_Date;
                    if (fixedDate) {
                        const m = moment(fixedDate);
                        if (m.year() > 2400) {
                            fixedDate = m.subtract(543, 'years').format('YYYY-MM-DD');
                        }
                    }

                    // Fix Status
                    let statusName = item.Rcv_Status;
                    if (item.Rcv_Status == 1) statusName = "ปกติ";
                    else if (item.Rcv_Status == 13) statusName = "ยกเลิก";
                    else if (item.Rcv_Status == 2) statusName = "ปิดงาน";

                    // Fix Name
                    let arName = item.AR_Name;
                    if (!arName && item.AR_Code) {
                        arName = arMap[item.AR_Code] || item.AR_Code;
                    }

                    return {
                        ...item,
                        Rcv_Date_Fixed: fixedDate,
                        Rcv_Status_Name: statusName,
                        AR_Name_Fixed: arName
                    };
                });

                setDataMasterList(processedList);
                setCurrentPage(1);
            }

            if (detailList && detailList.length > 0) {
                setDataDetailList(detailList);
            }
        } catch (error) {
            getAlert("FAILED", error.message);
        }
    };

    const filterItems = () => {
        if (searchTerm === "") {
            setFilteredItems(dataMasterList);
        } else {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            const filtered = dataMasterList.filter((item) =>
                Object.values(item).some(
                    (value) =>
                        typeof value === "string" &&
                        value.toLowerCase().includes(lowercasedSearchTerm)
                )
            );
            setFilteredItems(filtered);
        }
    };

    // filter วันที่
    const filterItemsByDate = async (type) => {
        if (allDates) {
            setFilteredItems(dataMasterList);
            return;
        }
        let filteredByDate = dataMasterList;

        if (startDate && endDate) {
            const start = moment(startDate, "YYYY-MM-DD").subtract(543, 'year');
            const end = moment(endDate, "YYYY-MM-DD").subtract(543, 'year').endOf("day");

            filteredByDate = dataMasterList.filter((item) => {
                const itemDate =
                    type === "documentDate"
                        ? moment(item.Rcv_Date_Fixed)
                        : type === "dueDate"
                            ? moment(item.Doc_DueDate)
                            : null;
                if (!itemDate || !itemDate.isValid()) return false;

                const isBetween = itemDate.isBetween(start, end, null, "[]");
                return isBetween;
            });
        }

        // Apply current sorting if set
        if (sortField) {
            filteredByDate.sort((a, b) => {
                let aValue = a[sortField] || "";
                let bValue = b[sortField] || "";

                if (sortOrder === "asc") {
                    return aValue.localeCompare(bValue);
                } else {
                    return bValue.localeCompare(aValue);
                }
            });
        }

        setFilteredItems(filteredByDate);
    };

    const handleSearch = () => {
        const type = selectedDateType === "1" ? "documentDate" : "dueDate";
        filterItemsByDate(type);
    };

    const handleClear = () => {
        setSearchTerm("");
        setStartDate(moment().startOf("month").add(543, 'year').format('YYYY-MM-DD'));
        setEndDate(moment().add(543, 'year').format('YYYY-MM-DD'));
        setSelectedDateType("1");
        setFilteredItems(dataMasterList);
        setSortField("");
        setSortOrder("desc");
    };

    const handleSort = () => {
        if (!sortField) return;
        const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
        setSortOrder(newSortOrder);

        const sortedItems = [...filteredItems].sort((a, b) => {
            if (newSortOrder === "asc") {
                return a[sortField].localeCompare(b[sortField]);
            } else {
                return b[sortField].localeCompare(a[sortField]);
            }
        });
        setFilteredItems(sortedItems);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <>
            <div className="page-header d-flex justify-content-between align-items-center mb-0">
                <Breadcrumbs
                    page={name}
                    items={[
                        { name: "งานบัญชี", url: "/account" },
                        { name: name, url: "/receipt-voucher" },
                    ]}
                />
            </div>
            <div className="row" style={{ maxHeight: "85vh", overflowY: "auto" }}>
                <div className="col-12">
                    <div
                        className="p-3 rounded-3 my-2 shadow-lg"
                        style={{ backgroundColor: "#ffff" }}
                    >
                        <div className="row  mb-2">
                            <div className="col-3">
                                <div className="navbar-form nav-search">
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            placeholder="ค้นหาเอกสาร..."
                                            className="form-control"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <span className="input-group-text">
                                            <i className="fas fa-search"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-7 d-flex">
                                <DateFilterTable
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    selectedDateType={selectedDateType}
                                    setSelectedDateType={setSelectedDateType}
                                    handleSearch={handleSearch}
                                    allDates={allDates}
                                    setAllDates={setAllDates}
                                />
                            </div>
                            <div className="col-2 d-flex justify-content-end">
                                <button
                                    onClick={onPageInsert}
                                    className="btn btn-warning text-white ms-2"
                                    type="button"
                                >
                                    สร้าง{name} <i className="fa fa-plus"></i>
                                </button>
                            </div>
                        </div>
                        <div
                            className="p-1 px-3 rounded-3 shadow-sm"
                            style={{ backgroundColor: "#f5f7fd" }}
                        >
                            <div className="row mb-2">
                                <small
                                    className="d-block p-1 fw-bold"
                                    style={{
                                        borderLeft: "5px solid #EF6C00",
                                        borderRadius: "3px",
                                    }}
                                >
                                    เรียงลำดับตาม
                                </small>

                                <div className="col-1 d-flex justify-content-between">
                                    <div className="radio-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={sortField === "Rcv_No"}
                                            onChange={() => setSortField("Rcv_No")}
                                        />
                                        <label className="text-black">เลขที่เอกสาร</label>
                                    </div>
                                </div>
                                <div className="col-1 d-flex justify-content-between">
                                    <div className="radio-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={sortField === "Rcv_Date"}
                                            onChange={() => setSortField("Rcv_Date")}
                                        />
                                        <label className="text-black">วันที่เอกสาร</label>
                                    </div>
                                </div>
                                <div className="col-1 d-flex justify-content-between">
                                    <div className="radio-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            checked={sortField === "Doc_DueDate"}
                                            onChange={() => setSortField("Doc_DueDate")}
                                        />
                                        <label className="text-black">Due Date</label>
                                    </div>
                                </div>
                                <div className="col-1 d-flex justify-content-between">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={handleSort}
                                        style={{
                                            cursor: "pointer",
                                            color: "black",
                                            backgroundColor: "transparent",
                                        }}
                                    >
                                        <i
                                            className={`fas fa-arrow-${sortOrder === "asc" ? "up" : "down"
                                                }`}
                                        ></i>{" "}
                                        Sort
                                    </button>
                                </div>
                                <div className="col-8 d-flex justify-content-end">
                                    <button
                                        onClick={handleClear}
                                        className="btn btn-danger text-white"
                                        type="button"
                                        style={{ padding: " 0.9rem" }}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <DataTable
                        currentItems={currentItems}
                        onRowSelected={onRowSelected}
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                        dataMasterList={filteredItems}
                        itemsPerPage={itemsPerPage}
                        fieldMappings={{
                            no: "Rcv_No",
                            refDoc: "Ref_Doc",
                            // typeName: "Rcv_Type", // 1=Inv, 2=Other
                            statusName: "Rcv_Status_Name", // 1=Receive, 13=Cancel
                            // statusColor: "PayStatus_Colour",
                            // payTypeName: "Pay_TypeName",
                            date: "Rcv_Date_Fixed",
                            apCode: "AR_Code",
                            apName: "AR_Name_Fixed", // Using Code for now as Name is not in H table
                            dueDate: "Doc_DueDate",
                            netTotal: "Total_Pay_Per",
                            createdBy: "Created_By_Name",
                            updatedBy: "Update_By_Name",
                            // File_Name: "File_Name",
                            Pay_Trans_Type: "Pay_Trans_Type",
                        }}
                        getReport={(docNo) => {
                            const item = dataMasterList.find(x => x.Rcv_No === docNo);
                            if (item) {
                                const transType = item.Pay_Trans_Type;
                                const pType = item.Rcv_Cash;
                                const isCash = (transType === 0 || transType === 1) || (pType === "RCV_CASH" || pType === "CASH" || pType === "TRANSFER");
                                const reportName = isCash ? "RCV_CASH" : "RCV_CHEQUE";
                                getReport(reportName, docNo);
                            } else {
                                getReport("RCV_CASH", docNo);
                            }
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default Main;
